import React, { useMemo, useState } from "react";
import { flexRender, SortingState, useTable } from "@tanstack/react-table";
import { cloneDeep } from "lodash";
import { FileSpreadsheet, FilterX } from "lucide-react";
import { CopyToClipboardButton } from "../CopyToClipboardButton";
import { exportToXlsx, sheetType } from "../../src/common/exportToXlsx";
import { MultiSelectDropdown } from "../../src/common/MultiSelectDropdown";
import { Filter, resolveFilterFnName } from "./ReactTableFilters";
import { reactTable2Features, ReactTable2Table } from "./features";
import { useTableFilters } from "./useTableFilters";

// https://tanstack.com/table/v9

// TypeScript interface for column meta properties (following TanStack Table official approach)
export interface ColumnMeta {
    className?: string;
}

// Safely get a className from a column definition (meta.className preferred, falls back to className).
const getColumnClassName = (columnDef: { meta?: ColumnMeta; className?: string; [key: string]: any }): string => {
    return columnDef?.meta?.className || columnDef?.className || "";
};

/**
 * react-table wrapper, v2.
 *
 * Lives alongside the original `../react-table/ReactTable`, which is unchanged. Pages opt in to the
 * redesigned filtering by importing `ReactTable2` instead of `ReactTable`.
 *
 *             <ReactTable2
 *              columns={columns}
 *              data={transactions}
 *              defaultHiddenColumns={["_id", "secondCurrency", "secondSettlementAmount", "secondInstrumentName"]}
 *             />
 *
 *  - 'defaultHiddenColumns' is optional. default value is 'null' - hidden columns select is not visible
 *  - use 'defaultHiddenColumns={[]}' when hidden columns select is needed and no default hidden columns
 *
 * Filtering: every column gets an Excel-style searchable multi-select by default. A column can opt into a
 * different filter declaratively - `filter: "select"`, `filter: { type: "select", options }`, "text",
 * "multiSelect", "zero", "none" - or by passing a render function (legacy component style).
 */
export interface Props {
    columns: any[];
    data: any;
    /** Optional table title. Rendered on the same row as the copy/XLSX buttons, like the legacy `Grid` header. */
    header?: React.ReactNode;
    defaultHiddenColumns?: any;
    initialPageSize?: number;
    /** When false, all rows are shown and pagination controls are hidden. Default true. */
    paginate?: boolean;
    exportToXlsxFile?: string;
    copyButton?: boolean;
}

const defaultColumn: any = { size: 50, minSize: 1, maxSize: 500 };

const formatCellValueForCopy = (value: unknown): string => {
    if (value === null || typeof value === "undefined") {
        return "";
    }
    if (Array.isArray(value)) {
        return value.map((item) => formatCellValueForCopy(item)).join(", ");
    }
    return String(value);
};

// Quote values that contain the delimiter, quotes or line breaks so Excel keeps them in a single cell
// instead of spilling into new columns/rows (RFC 4180 style, which Excel honors on paste).
const escapeForClipboard = (value: string, delimiter: string): string => {
    if (value.includes(delimiter) || value.includes("\n") || value.includes("\r") || value.includes('"')) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
};

export const buildCopyText = (table: ReactTable2Table, delimiter = "\t"): string => {
    const visibleColumns = table.getVisibleLeafColumns();
    const headers = visibleColumns.map((column) => {
        const header = column.columnDef.header;
        return escapeForClipboard(typeof header === "string" ? header : column.id, delimiter);
    });
    const rows = table
        .getPrePaginatedRowModel()
        .rows.map((row) =>
            visibleColumns.map((column) => escapeForClipboard(formatCellValueForCopy(row.getValue(column.id)), delimiter)).join(delimiter)
        );
    return [headers.join(delimiter), ...rows].join("\n");
};

export const ReactTable2 = ({
    columns,
    data: dataIn,
    header = null,
    defaultHiddenColumns = null,
    initialPageSize = 60,
    paginate = true,
    exportToXlsxFile = "",
    copyButton = true
}: Props): React.ReactElement => {
    // Issue with filter if first row has any value that is null, see https://github.com/TanStack/table/issues/4919
    const data = React.useMemo(() => {
        const dataNoNullFirstRow = cloneDeep(dataIn);
        if (dataNoNullFirstRow && dataNoNullFirstRow.length) {
            Object.keys(dataNoNullFirstRow[0]).forEach((key) => {
                if (dataNoNullFirstRow[0][key] === null) {
                    dataNoNullFirstRow[0][key] = "";
                }
            });
        }

        return dataNoNullFirstRow;
    }, [dataIn]);

    // Filter components assign columnDef.filterFn while rendering, but that happens after the row model
    // is computed. When a parent recreates its column defs (e.g. a columns useMemo depending on
    // queryArgs), the fresh defs lose that assignment and TanStack falls back to the "auto" filterFn,
    // which stringifies array filter values and matches nothing. Resolve the filterFn from the filter
    // spec (falling back to the default filter) up front so filtering always uses the right function.
    const tableColumns = useMemo(
        () =>
            columns.map((c) => {
                if (c.filterFn) {
                    return c;
                }
                const filterFn = resolveFilterFnName(c.filter);
                return filterFn ? { ...c, filterFn } : c;
            }),
        [columns]
    );

    const { columnFilters, setColumnFilters, hasActiveFilters, clearAllFilters } = useTableFilters();

    const [hiddenColumns, setHiddenColumnsState] = useState<string[]>(() => {
        const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
        return defaultHiddenColumns ? defaultHiddenColumns.filter((c: string) => !params.has(c)) : [];
    });

    const [sorting, setSorting] = useState<SortingState>([]);
    const [clipboard, setClipboard] = useState<string | null>(null);
    const copyButtonId = useMemo(() => `rt-copy-${Math.floor(Math.random() * 10000)}`, []);
    const columnVisibility = Object.fromEntries(columns.map((x) => [x.accessorKey, !hiddenColumns.includes(x.accessorKey)]));

    const table = useTable({
        features: reactTable2Features,
        data,
        columns: tableColumns,
        defaultColumn,
        state: {
            sorting,
            columnFilters,
            columnVisibility
        },
        initialState: { pagination: { pageIndex: 0, pageSize: paginate ? initialPageSize : Infinity } },
        enableColumnResizing: true,
        columnResizeMode: "onChange",
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters
    });

    const hiddenColumnsDropdownOptions = columns.map((c) => {
        // at least one column must be visible
        return {
            key: c.accessorKey,
            text: c.header,
            hidden: hiddenColumns && hiddenColumns.includes(c.accessorKey),
            disabled: hiddenColumns && !hiddenColumns.includes(c.accessorKey) && columns.length - 2 < hiddenColumns.length
        };
    });

    return (
        <div className="captor-table table-responsive">
            {header || exportToXlsxFile || copyButton ? (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "4px",
                        marginBottom: "0.5rem",
                        width: "100%"
                    }}
                >
                    {header ? <h4 style={{ margin: 0 }}>{header}</h4> : <div />}
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        {exportToXlsxFile ? (
                            <div
                                title="Export to XLSX"
                                style={{ width: "20px", height: "20px" }}
                                onClick={() => {
                                    const currentRecords: sheetType = table.getFilteredRowModel().rows.map((row) => {
                                        const item: any = row.original;
                                        delete item.__typename;
                                        return item;
                                    });
                                    exportToXlsx([currentRecords], exportToXlsxFile);
                                }}
                            >
                                <FileSpreadsheet className="Excel" />
                            </div>
                        ) : null}
                        {copyButton ? (
                            <CopyToClipboardButton
                                id={copyButtonId}
                                clipboard={clipboard}
                                className="grid-icon-button float-end"
                                onClick={() => setClipboard(buildCopyText(table))}
                            />
                        ) : null}
                    </div>
                </div>
            ) : null}

            {defaultHiddenColumns !== null || hasActiveFilters ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.5rem" }}>
                    {defaultHiddenColumns !== null ? (
                        <MultiSelectDropdown
                            key="hiddenColumnsDropdown"
                            className="captor-table-select"
                            placeholder="Hidden columns"
                            options={hiddenColumnsDropdownOptions}
                            defaultSelectedOptions={hiddenColumnsDropdownOptions.filter((x) => x.hidden).map((c) => c.key)}
                            onOptionSelect={(data) => {
                                setHiddenColumnsState(data);
                            }}
                        ></MultiSelectDropdown>
                    ) : null}
                    {hasActiveFilters ? (
                        <button
                            type="button"
                            title="Clear all filters"
                            className="inline-flex items-center gap-1 rounded border border-blue-600 bg-white px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                            onClick={() => clearAllFilters(table)}
                        >
                            <FilterX size={14} />
                            Clear filters
                        </button>
                    ) : null}
                </div>
            ) : null}

            <table className="table table-striped table-xs" role="table">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="tr" role="row">
                            {headerGroup.headers.map((header) => {
                                return (
                                    <th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        style={header.getSize() > 0 ? { width: header.getSize() } : undefined}
                                        className={`th ${getColumnClassName(header.column.columnDef)}`}
                                        role="columnheader"
                                    >
                                        {header.column.getCanResize() && (
                                            <div
                                                onMouseDown={header.getResizeHandler()}
                                                className={`resizer ${header.column.getIsResizing() ? "isResizing" : ""}`}
                                            ></div>
                                        )}

                                        {header.isPlaceholder ? null : (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    gap: "4px",
                                                    minWidth: 0
                                                }}
                                            >
                                                <div
                                                    title="Toggle SortBy"
                                                    {...{
                                                        className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                                                        onClick: header.column.getToggleSortingHandler(),
                                                        style: {
                                                            minWidth: 0,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }
                                                    }}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {{
                                                        asc: " ▼",
                                                        desc: " ▲"
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </div>
                                                {header.column.getCanFilter() ? (
                                                    // flex-1 hands the cell's leftover width to the filter trigger so the whole
                                                    // empty area right of the label opens the dropdown. The trigger must not use
                                                    // negative margins to widen itself: `.captor-table th` is overflow:hidden.
                                                    <div
                                                        className="flex min-w-0 flex-1 justify-end self-stretch"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Filter column={header.column} table={table} />
                                                    </div>
                                                ) : null}
                                            </div>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody role="rowgroup">
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="tr" role="row">
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    key={cell.id}
                                    className={`td ${getColumnClassName(cell.column.columnDef)}`}
                                    role="cell"
                                    style={{ width: cell.column.getSize() }}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {paginate ? (
                <div className="pagination">
                    <div>
                        <button
                            className="button"
                            onClick={() => table.setPageIndex(0)}
                            disabled={!table.getCanPreviousPage()}
                            title="First"
                        >
                            &laquo;
                        </button>{" "}
                        <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} title="Previous">
                            &lt;
                        </button>{" "}
                    </div>

                    <div className="ms-2">
                        <span>
                            Page:{" "}
                            <input
                                type="number"
                                min={1}
                                max={table.getPageCount()}
                                value={table.state.pagination.pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    table.setPageIndex(page);
                                }}
                                style={{ maxWidth: "70px" }}
                            />{" "}
                            of {table.getPageCount()}
                        </span>
                    </div>

                    <div className="ms-2">
                        <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} title="Next">
                            &gt;
                        </button>{" "}
                        <button
                            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                            disabled={!table.getCanNextPage()}
                            title="Last"
                        >
                            &raquo;
                        </button>
                    </div>

                    <div className="ms-5">
                        <select
                            value={table.state.pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                        >
                            {(() => {
                                const defaultPageSizes = [20, 40, 60, 80, 100];
                                const currentPageSize = table.state.pagination.pageSize;
                                const pageSizes = defaultPageSizes.includes(currentPageSize)
                                    ? defaultPageSizes
                                    : [...defaultPageSizes, currentPageSize].sort((a, b) => a - b);
                                return pageSizes.map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        Show {pageSize}
                                    </option>
                                ));
                            })()}
                        </select>
                    </div>
                </div>
            ) : null}
        </div>
    );
};
